import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  X, Store, MapPin, Navigation, ExternalLink, Clock3, Phone, Mail, Globe, Facebook,
  Instagram, MessageCircleMore, Link2, Star, Briefcase, Wallet, Layers, CalendarDays,
  Wrench, Droplet, Droplets, CircleDot, BatteryCharging, Snowflake, ScanLine, Sparkles,
  CheckCircle2, XCircle, User,
} from "lucide-react";
import { StatusBadge, useDialog, DialogFrame, DialogSection as Section } from "./ui";

const FONT_HEADING = { fontFamily: "'NeueMontreal', sans-serif" };
const WEEK = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const CATEGORY_ICONS = {
  "Oil Change": Droplet,
  "Car Wash": Droplets,
  Detailing: Sparkles,
  Tyres: CircleDot,
  Repair: Wrench,
  Battery: BatteryCharging,
  "AC Service": Snowflake,
  Diagnostics: ScanLine,
};

// Social link types the provider app offers: website, facebook, instagram, whatsapp, email
const SOCIAL = {
  website: { Icon: Globe, color: "#4A90E2", href: (u) => (/^https?:\/\//.test(u) ? u : `https://${u}`) },
  facebook: { Icon: Facebook, color: "#1877F2", href: (u) => (/^https?:\/\//.test(u) ? u : `https://${u}`) },
  instagram: { Icon: Instagram, color: "#E4405F", href: (u) => (/^https?:\/\//.test(u) ? u : `https://instagram.com/${u.replace(/^@/, "")}`) },
  whatsapp: { Icon: MessageCircleMore, color: "#25D366", href: (u) => `https://wa.me/${u.replace(/[^\d]/g, "")}` },
  email: { Icon: Mail, color: "#6B7280", href: (u) => `mailto:${u}` },
};

const toMinutes = (hhmm) => {
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + m;
};
const is24h = (wh) => wh.startTime === "00:00" && wh.endTime === "23:59";
const formatTime = (hhmm) => {
  const [h, m] = hhmm.split(":").map(Number);
  const suffix = h >= 12 ? "PM" : "AM";
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${suffix}`;
};

function getOpenState(workingHours, now = new Date()) {
  const today = WEEK[(now.getDay() + 6) % 7];
  const wh = workingHours.find((w) => w.day === today && w.isOpen);
  if (!wh) return { today, open: false };
  if (is24h(wh)) return { today, open: true };
  const mins = now.getHours() * 60 + now.getMinutes();
  return { today, open: mins >= toMinutes(wh.startTime) && mins <= toMinutes(wh.endTime) };
}

export default function ShopDialog({ shop, onClose }) {
  const closeRef = useRef(null);
  const [category, setCategory] = useState("All");

  useDialog(!!shop, onClose);
  useEffect(() => {
    if (!shop) return;
    setCategory("All");
    closeRef.current?.focus();
  }, [shop]);

  const grouped = useMemo(() => {
    if (!shop) return {};
    return shop.services.reduce((acc, s) => {
      (acc[s.serviceName] = acc[s.serviceName] || []).push(s);
      return acc;
    }, {});
  }, [shop]);

  if (!shop) return null;

  const location = shop.locations.find((l) => l.isPrimary) || shop.locations[0];
  const openState = getOpenState(shop.workingHours);
  const categories = Object.keys(grouped);
  const visibleCategories = category === "All" ? categories : [category];
  const available = shop.services.filter((s) => s.isAvailable).length;
  const mapsUrl = location ? `https://www.google.com/maps?q=${location.latitude},${location.longitude}` : null;

  return (
    <DialogFrame labelledBy="shop-dialog-title" onClose={onClose}>
      {/* Cover + header */}
      <div className="relative shrink-0 h-44 sm:h-40 overflow-hidden">
        <ShopCover shop={shop} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(15,15,20,0.15) 0%, rgba(15,15,20,0.85) 100%)" }} />
        <button
          ref={closeRef}
          type="button"
          aria-label="Close"
          onClick={onClose}
          className="absolute z-10 top-4 right-4 w-9 h-9 rounded-xl bg-[#0000004d] hover:bg-[#00000080] backdrop-blur flex items-center justify-center transition"
        >
          <X size={18} className="text-[#fff]" />
        </button>
        <div className="absolute left-6 right-6 bottom-4 sm:left-4 sm:right-4 text-[#fff]">
          <div className="flex items-center gap-2 flex-wrap">
            <StatusBadge status={shop.status} />
            <span
              className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                openState.open ? "bg-[#DCFCE7] text-[#16A34A]" : "bg-[#F3F4F6] text-[#6B7280]"
              }`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${openState.open ? "bg-[#16A34A]" : "bg-[#9CA3AF]"}`} />
              {openState.open ? "Open now" : "Closed now"}
            </span>
          </div>
          <h2 id="shop-dialog-title" className="m-0 mt-2 text-2xl sm:text-xl font-medium" style={FONT_HEADING}>
            {shop.shopName}
          </h2>
          <p className="m-0 mt-0.5 text-xs text-[#ffffffcc] inline-flex items-center gap-1.5">
            <MapPin size={13} /> {location?.address || "No location set"}
          </p>
        </div>
      </div>

      {/* Stats strip */}
      <div className="shrink-0 grid grid-cols-5 md:grid-cols-3 sm:grid-cols-2 gap-px bg-[#EEEEF1] border-b border-[#EEEEF1]">
        <Stat Icon={Layers} label="Services" value={`${available}/${shop.services.length} live`} />
        <Stat Icon={Store} label="Categories" value={categories.length} />
        <Stat Icon={Briefcase} label="Jobs done" value={shop.jobs} />
        <Stat Icon={Star} label="Rating" value={shop.rating} star />
        <Stat Icon={Wallet} label="Revenue" value={shop.revenueLabel} />
      </div>

      {/* Body */}
      <div className="flex-1 min-h-0 overflow-y-auto grid grid-cols-[1fr_1.15fr] md:grid-cols-1 gap-5 p-6 sm:p-4">
        <div className="space-y-4 min-w-0">
          <Section title="Provider">
            <div className="flex items-center gap-3">
              <span className="w-11 h-11 rounded-2xl bg-[#1B1B22] flex items-center justify-center shrink-0">
                <User size={20} className="text-[#fff]" />
              </span>
              <div className="min-w-0">
                <p className="m-0 text-base font-semibold text-[#111]">{shop.provider.name}</p>
                <p className="m-0 text-xs text-[#9CA3AF] inline-flex items-center gap-1">
                  <CalendarDays size={12} /> Shop created {shop.createdAt}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-1 gap-2 mt-4">
              <Line Icon={Phone} value={shop.provider.phone} />
              <Line Icon={Mail} value={shop.provider.email} />
            </div>
          </Section>

          <Section
            title="Location"
            action={mapsUrl && (
              <a
                href={mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="no-underline inline-flex items-center gap-1 text-xs font-semibold text-[#EA352B] hover:text-[#C92A21]"
              >
                Open in Maps <ExternalLink size={12} />
              </a>
            )}
          >
            {location ? (
              <>
                <div className="rounded-2xl overflow-hidden border border-[#EEEEF1] h-44 bg-[#F1F1F4]">
                  <iframe
                    title={`Map of ${shop.shopName}`}
                    src={`https://maps.google.com/maps?q=${location.latitude},${location.longitude}&z=15&output=embed`}
                    className="w-full h-full border-0"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
                <div className="mt-3 flex items-start gap-2">
                  <MapPin size={15} className="text-[#EA352B] mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <p className="m-0 text-sm text-[#111]">{location.address}</p>
                    <p className="m-0 mt-0.5 text-[11px] text-[#9CA3AF] inline-flex items-center gap-1">
                      <Navigation size={11} /> {location.latitude.toFixed(4)}, {location.longitude.toFixed(4)} · {location.title}
                    </p>
                  </div>
                </div>
              </>
            ) : (
              <p className="m-0 text-sm text-[#9CA3AF]">The provider hasn’t set a location.</p>
            )}
          </Section>

          <Section title="Business timing">
            <ul className="m-0 pl-0 list-none space-y-1.5">
              {WEEK.map((day) => {
                const wh = shop.workingHours.find((w) => w.day === day && w.isOpen);
                const isToday = day === openState.today;
                return (
                  <li
                    key={day}
                    className={`flex items-center justify-between gap-3 rounded-xl px-3 py-2 text-sm ${
                      isToday ? "bg-[#FFF7F6] border border-[#F6C9C5]" : "bg-[#F7F7F9] border border-transparent"
                    }`}
                  >
                    <span className={`inline-flex items-center gap-2 ${wh ? "text-[#111]" : "text-[#9CA3AF]"}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${wh ? "bg-[#16A34A]" : "bg-[#D1D5DB]"}`} />
                      {day}
                      {isToday && <span className="text-[10px] font-semibold text-[#EA352B]">Today</span>}
                    </span>
                    {!wh ? (
                      <span className="text-xs text-[#9CA3AF]">Closed</span>
                    ) : is24h(wh) ? (
                      <span className="text-[11px] font-semibold rounded-full px-2 py-0.5 bg-[#ECFDF5] text-[#059669]">Open 24 hours</span>
                    ) : (
                      <span className="text-xs font-medium text-[#374151] inline-flex items-center gap-1">
                        <Clock3 size={12} className="text-[#9CA3AF]" />
                        {formatTime(wh.startTime)} – {formatTime(wh.endTime)}
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          </Section>
        </div>

        <div className="space-y-4 min-w-0">
          <Section title={`Services · ${shop.services.length}`}>
            {categories.length > 1 && (
              <div className="flex gap-2 flex-wrap mb-4">
                {["All", ...categories].map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setCategory(c)}
                    className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${
                      category === c ? "bg-[#EA352B] text-[#fff]" : "bg-[#F3F3F6] text-[#6B7280] hover:text-[#111]"
                    }`}
                  >
                    {c}
                    {c !== "All" && <span className="ml-1 opacity-70">{grouped[c].length}</span>}
                  </button>
                ))}
              </div>
            )}

            <div className="space-y-5">
              {visibleCategories.map((cat) => {
                const CatIcon = CATEGORY_ICONS[cat] || Wrench;
                return (
                  <div key={cat}>
                    <div className="flex items-center gap-2 mb-2.5">
                      <span className="w-7 h-7 rounded-lg bg-[#FDECEA] flex items-center justify-center">
                        <CatIcon size={14} className="text-[#EA352B]" />
                      </span>
                      <span className="text-sm font-semibold text-[#111]">{cat}</span>
                      <span className="text-[11px] text-[#9CA3AF]">{grouped[cat].length} services</span>
                    </div>
                    <div className="space-y-2">
                      {grouped[cat].map((s) => (
                        <ServiceItem key={s.title} service={s} Icon={CatIcon} />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </Section>

          <Section title="Social links">
            {shop.socialLinks.length === 0 ? (
              <p className="m-0 text-sm text-[#9CA3AF]">No links added by the provider.</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-1 gap-2">
                {shop.socialLinks.map((l) => {
                  const cfg = SOCIAL[l.type] || { Icon: Link2, color: "#6B7280", href: (u) => u };
                  return (
                    <a
                      key={`${l.type}-${l.url}`}
                      href={cfg.href(l.url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="no-underline group flex items-center gap-3 rounded-xl border border-[#EEEEF1] px-3 py-2.5 hover:border-[#D9DAE0] hover:shadow-sm transition min-w-0"
                    >
                      <span className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${cfg.color}1f` }}>
                        <cfg.Icon size={17} style={{ color: cfg.color }} />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-[11px] text-[#9CA3AF] capitalize">{l.title}</span>
                        <span className="block text-[13px] font-medium text-[#111] truncate">{l.url}</span>
                      </span>
                      <ExternalLink size={13} className="text-[#C0C4CC] group-hover:text-[#EA352B] shrink-0" />
                    </a>
                  );
                })}
              </div>
            )}
          </Section>
        </div>
      </div>
    </DialogFrame>
  );
}

function ShopCover({ shop }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [shop.shopImage]);

  if (shop.shopImage && !failed) {
    return <img src={shop.shopImage} alt={shop.shopName} onError={() => setFailed(true)} className="w-full h-full object-cover" />;
  }
  // No cover uploaded (or it failed to load): branded placeholder
  return (
    <div className="w-full h-full relative" style={{ background: "linear-gradient(120deg, #1B1B22 0%, #2A1614 55%, #EA352B 130%)" }}>
      <div className="absolute -right-10 -top-10 w-56 h-56 rounded-full bg-[#EA352B] opacity-25" />
      <div className="absolute right-40 -bottom-16 w-40 h-40 rounded-full bg-[#FF8A3D] opacity-15" />
      <Store size={120} className="absolute right-8 top-6 text-[#ffffff14]" />
    </div>
  );
}

function Stat({ Icon, label, value, star }) {
  return (
    <div className="bg-[#fff] px-4 py-3 min-w-0">
      <span className="flex items-center gap-1.5 text-[11px] text-[#9CA3AF]">
        <Icon size={12} className={star ? "text-[#F5B400] fill-[#F5B400]" : "text-[#EA352B]"} /> {label}
      </span>
      <span className="block mt-0.5 text-sm font-semibold text-[#111] truncate">{value}</span>
    </div>
  );
}

function Line({ Icon, value }) {
  return (
    <span className="flex items-center gap-2 rounded-xl bg-[#F7F7F9] px-3 py-2 text-xs text-[#374151] min-w-0">
      <Icon size={14} className="text-[#EA352B] shrink-0" />
      <span className="truncate">{value || "Not provided"}</span>
    </span>
  );
}

function ServiceItem({ service, Icon }) {
  const [failed, setFailed] = useState(false);
  return (
    <div className={`flex gap-3 rounded-2xl border p-3 ${service.isAvailable ? "border-[#EEEEF1] bg-[#fff]" : "border-dashed border-[#E4E4E9] bg-[#FAFAFB]"}`}>
      <span className="w-14 h-14 rounded-xl overflow-hidden bg-[#F3F3F6] flex items-center justify-center shrink-0">
        {service.serviceImage && !failed ? (
          <img src={service.serviceImage} alt="" onError={() => setFailed(true)} className="w-full h-full object-cover" />
        ) : (
          <Icon size={20} className="text-[#C0C4CC]" />
        )}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <p className={`m-0 text-sm font-medium ${service.isAvailable ? "text-[#111]" : "text-[#9CA3AF]"}`}>{service.title}</p>
          {service.isAvailable ? (
            <span className="shrink-0 inline-flex items-center gap-1 text-[10px] font-semibold text-[#16A34A]">
              <CheckCircle2 size={11} /> Available
            </span>
          ) : (
            <span className="shrink-0 inline-flex items-center gap-1 text-[10px] font-semibold text-[#9CA3AF]">
              <XCircle size={11} /> Unavailable
            </span>
          )}
        </div>
        {service.description && <p className="m-0 mt-0.5 text-xs text-[#6B7280] leading-snug">{service.description}</p>}
      </div>
    </div>
  );
}
